import { Box, Stack, SxProps, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Dialog } from "components/Dialog";
import { FacilityPicker } from "components/FacilityPicker";
import { CheckboxField } from "components/Forms/CheckboxField";
import { DateField } from "components/Forms/DateField";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { IssueListButton } from "components/Forms/IssueListButton";
import { RadioGroupField } from "components/Forms/RadioGroupField";
import { NaicsPicker } from "components/NaicsPicker";
import { TagPicker } from "components/TagPicker";
import { BILLING_CONTACT_PERMISSION_ERROR_CODE } from "encamp-shared/src/constants/permissionsTypes";
import { BILLING_CONTACT_STATES } from "encamp-shared/src/constants/states";
import { safeParseFloat } from "encamp-shared/src/tierii/utilities";
import { HELD_ALTERNATE_ID_ERROR_CODE } from "encamp-shared/src/violations/expiredAltIdMatchKinds";
import { FacilityInput, FacilityStatus, Issue, TagType } from "generated-graphql/graphql";
import { stripPhoneNumberMask } from "hooks/transform/utils";
import { useCanEditFacilityBillingContact } from "hooks/useAuthorization";
import { useTenant } from "hooks/useTenant";
import { useValidatingForm } from "hooks/useValidatingForm";
import { isEqual } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, FormProvider, useFormContext } from "react-hook-form";
import { useFacilityInputValidation } from "routes/Customer/Chemicals/Report/validationHooks/useFacilityInputValidation";
import { hasCriticalIssues } from "util/forms";
import { getMessageForErrorCodes, getUserFacingMessage } from "utils/errorUtils";

import {
  basicInformationKeys,
  addressKeys,
  useAddressIssues,
  useBasicInformationIssues,
  useFacility,
  useUpdateFacilityProfile,
} from "../useFacility";
import { AddressGrid } from "./AddressGrid";
import { BASICS_SECTION_ICON } from "./BasicsSection";
import { GovernmentIdFields } from "./GovernmentIdFields";
import {
  getMailingAddressUpdatesForSelection,
  MailingAddressSelection,
} from "./mailingAddressSync";

type EditBasicsFormData = Omit<FacilityInput, "stateFields"> & {
  isColocated?: boolean;
};

const gridSx: SxProps = {
  display: "grid",
  gridTemplateColumns: "repeat(12, 1fr)",
  columnGap: 2,
  rowGap: 0,
  "& .MuiFormHelperText-root.Mui-error": {
    mb: 1,
  },
};

export function EditBasicsDialog({ onClose }: { onClose: () => void }) {
  const alerts = useAlerts();
  const { data } = useFacility();
  const facility = data?.facility;
  const [updateFacility, { loading: saving }] = useUpdateFacilityProfile();

  const defaultValues = useMemo<EditBasicsFormData>(
    () => ({
      name: facility?.name ?? "",
      customerFacilityId: facility?.customerFacilityId ?? "",
      companyName: facility?.companyName ?? null,
      department: facility?.department ?? null,
      businessUnit: facility?.businessUnit ?? null,
      phone: facility?.phone ?? null,
      phone24Hour: facility?.phone24Hour ?? null,
      dbNumber: facility?.dbNumber ?? null,
      naicsCode: facility?.naicsCode ?? null,
      tags: facility?.tags ?? [],

      // Regulatory flags (hidden — round-tripped to avoid nulling on save)
      isSubjectToChemicalAccidentPrevention:
        facility?.isSubjectToChemicalAccidentPrevention ?? null,
      isSubjectToEmergencyPlanning: facility?.isSubjectToEmergencyPlanning ?? null,
      isSubjectToToxicsReleaseInventoryReporting:
        facility?.isSubjectToToxicsReleaseInventoryReporting ?? null,

      // Operational Status
      facilityType: facility?.facilityType,
      status: facility?.status,
      statusReason: facility?.statusReason,
      dateOpened: facility?.dateOpened ?? null,
      dateClosed: facility?.dateClosed ?? null,
      dateInactive: facility?.dateInactive ?? null,
      maxNumOccupants: facility?.maxNumOccupants ?? null,
      isUnmanned: facility?.isUnmanned ? true : false,
      isColocated: facility?.colocationReportingFacility ? true : false,
      colocationReportingFacilityId: facility?.colocationReportingFacility?.id ?? null,

      // Physical Address
      streetAddress1: facility?.streetAddress1 ?? null,
      streetAddress2: facility?.streetAddress2 ?? null,
      city: facility?.city ?? null,
      state: facility?.state ?? null,
      zip: facility?.zip ?? null,
      county: facility?.county ?? null,
      municipality: facility?.municipality ?? null,
      country: facility?.country ?? null,
      latitude: facility?.latitude ?? null,
      longitude: facility?.longitude ?? null,

      // Mailing Address
      isMailingAddressSameAsFacility: facility?.isMailingAddressEncamp
        ? "encamp"
        : facility?.isMailingAddressSameAsFacility
          ? true
          : false,
      mailingStreetAddress1: facility?.mailingStreetAddress1 ?? null,
      mailingStreetAddress2: facility?.mailingStreetAddress2 ?? null,
      mailingCity: facility?.mailingCity ?? null,
      mailingState: facility?.mailingState ?? null,
      mailingZip: facility?.mailingZip ?? null,
      mailingCountry: facility?.mailingCountry ?? null,
      mailingCompanyName: facility?.mailingCompanyName ?? null,

      // Billing
      useEncampBillingContact: facility?.useEncampBillingContact ?? false,

      // Government IDs
      facilityAlternateIds: facility?.facilityAlternateIds ?? [],
    }),
    [facility]
  );

  const basicInformationIssues = useBasicInformationIssues();
  const addressIssues = useAddressIssues();

  const persistedIssues = useMemo(
    () => [...basicInformationIssues, ...addressIssues],
    [basicInformationIssues, addressIssues]
  );

  const validationPick = useMemo(() => [...basicInformationKeys, ...addressKeys], []);

  const validator = useFacilityInputValidation({
    facilityId: facility?.id ?? "",
    reportId: undefined,
    pick: validationPick,
  });

  // Wrap the validator to bridge the type difference between
  // EditBasicsFormData and FacilityDetailsInput
  const formValidator = useCallback(
    async (input: EditBasicsFormData): Promise<Issue[]> => {
      // The validator internally calls transform() which handles the
      // conversion to FacilityInput. Our form data shape is compatible.
      const issues = await validator(input as never);
      // Filter out state field issues that leak through because the
      // compliance engine's pick filter doesn't gate state field validation.
      return issues.filter((issue) => issue.modelName !== "FacilityStateField");
    },
    [validator]
  );

  const form = useValidatingForm<EditBasicsFormData>(defaultValues, persistedIssues, formValidator);
  const { handleSubmit, issues } = form;

  const [showConfirmClose, setShowConfirmClose] = useState(false);

  const currentValues = form.watch();
  const hasUnsavedChanges = useMemo(() => {
    /** Recursively normalize form values for comparison:
     *  - "" → null (empty strings treated as null)
     *  - false/undefined → null for nullable booleans
     *  - phone fields stripped to digits only */
    const normalizeValue = (val: unknown): unknown => {
      if (val === "" || val === undefined) return null;
      if (Array.isArray(val)) return val.map(normalizeValue);
      if (val !== null && typeof val === "object") {
        return Object.fromEntries(Object.entries(val).map(([k, v]) => [k, normalizeValue(v)]));
      }
      return val;
    };
    const normalizePhone = (val: string | null | undefined) => {
      const digits = val?.replace(/\D/g, "").replace(/^1/, "") ?? "";
      return digits || null;
    };
    const normalize = (vals: EditBasicsFormData) =>
      normalizeValue({
        ...vals,
        phone: normalizePhone(vals.phone),
        phone24Hour: normalizePhone(vals.phone24Hour),
      });
    return !isEqual(normalize(currentValues), normalize(defaultValues));
  }, [currentValues, defaultValues]);

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  const onSubmit = useCallback(
    async (formData: EditBasicsFormData) => {
      if (!facility) return;

      const { isColocated, ...rest } = formData;
      const input: FacilityInput = {
        ...rest,
        latitude: safeParseFloat(String(formData.latitude ?? "")),
        longitude: safeParseFloat(String(formData.longitude ?? "")),
        colocationReportingFacilityId: isColocated ? formData.colocationReportingFacilityId : null,
        facilityAlternateIds: formData.facilityAlternateIds?.map((altId) => ({
          ...altId,
          type: altId.type ?? "",
          value: altId.hasNoId ? "" : (altId.value ?? ""),
          expiresAt: altId.hasNoId ? null : (altId.expiresAt ?? null),
        })),
        phone: stripPhoneNumberMask(formData.phone),
        phone24Hour: stripPhoneNumberMask(formData.phone24Hour),
      };

      try {
        await updateFacility({
          variables: {
            id: facility.id,
            facility: input,
          },
        });
        alerts.success("Facility information saved successfully.");
        onClose();
      } catch (err) {
        // Prefer the server's own wording when it wrote one for this user. The
        // expired-ID refusal names a remedy, so it stays on screen rather than
        // auto-dismissing.
        const pinned = getMessageForErrorCodes(err, [
          HELD_ALTERNATE_ID_ERROR_CODE,
          BILLING_CONTACT_PERMISSION_ERROR_CODE,
        ]);
        const refusal = pinned ?? getUserFacingMessage(err);
        alerts.error(refusal ?? "Error saving facility information.", undefined, {
          autoHideDuration: pinned ? null : undefined,
        });
        console.info("Error saving facility information.", err);
      }
    },
    [facility, updateFacility, alerts, onClose]
  );

  return (
    <>
      <Dialog
        open
        title={
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="h6">Edit</Typography>
            <Typography variant="h6">{BASICS_SECTION_ICON}</Typography>
            <Typography variant="h6">Basics</Typography>
          </Stack>
        }
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        primaryActionButtonProps={{
          loading: saving,
          disabled: !hasUnsavedChanges || hasCriticalIssues(issues),
          onClick: () => handleSubmit(onSubmit)(),
        }}
        additionalPrimaryActionElements={[
          <IssueListButton issues={issues} key="issue-list-button" />,
        ]}
      >
        <FormProvider {...form}>
          <form>
            <Stack spacing={0}>
              <BasicInfoFields />
              <OperationalStatusFields />
              <PhysicalAddressFields />
              <MailingAddressFields />
              {facility?.state && BILLING_CONTACT_STATES.includes(facility.state) && (
                <BillingFields />
              )}
              <GovernmentIdFields isEpaIdLocked={facility?.isEpaIdLocked ?? false} />
            </Stack>
          </form>
        </FormProvider>
      </Dialog>
      <ConfirmDialog
        open={showConfirmClose}
        onClose={() => setShowConfirmClose(false)}
        onConfirm={onClose}
        header="You have unsaved changes. Are you sure you want to close?"
        confirmText="Close without saving"
      />
    </>
  );
}

function BasicInfoFields() {
  const { control } = useFormContext<EditBasicsFormData>();
  const { data } = useFacility();

  return (
    <Box sx={gridSx}>
      <Box sx={{ gridColumn: "span 12" }}>
        <Typography variant="subtitle2" fontWeight={600} mb={3}>
          Basic Information
        </Typography>
      </Box>
      <Box sx={{ gridColumn: "span 12" }}>
        <FormTextField name="name" label="Facility Name*" control={control} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormTextField name="customerFacilityId" label="Facility ID" control={control} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormTextField name="companyName" label="Company Name" control={control} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormTextField name="department" label="Department" control={control} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormTextField name="businessUnit" label="Business Unit" control={control} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormTextField
          name="phone"
          label="Phone Number *"
          control={control}
          inputMask={data?.facility?.renderPhone?.inputMask ?? undefined}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormTextField
          name="phone24Hour"
          label="24-Hour Phone Number *"
          control={control}
          inputMask={data?.facility?.renderPhone24Hour?.inputMask ?? undefined}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormTextField name="dbNumber" label="Dun & Bradstreet Number" control={control} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <Controller
          name="naicsCode"
          control={control}
          render={({ field, fieldState }) => (
            <NaicsPicker
              {...fieldState}
              value={field.value ?? null}
              onChange={field.onChange}
              disabled={false}
            />
          )}
        />
      </Box>
      <Box sx={{ gridColumn: "span 12" }}>
        <Controller
          name="tags"
          control={control}
          render={({ field, fieldState }) => (
            <TagPicker
              {...fieldState}
              value={field.value ?? undefined}
              onChange={field.onChange}
              label="Tags"
              tagType={TagType.Facility}
            />
          )}
        />
      </Box>
    </Box>
  );
}

function OperationalStatusFields() {
  const { control, getValues, setValue, watch } = useFormContext<EditBasicsFormData>();
  const { tenant } = useTenant();
  const { data } = useFacility();
  const isColocated = watch("isColocated");
  const [colocationReportingFacility, setColocationReportingFacility] = useState(
    data?.facility?.colocationReportingFacility ?? null
  );

  return (
    <Box sx={gridSx}>
      <Box sx={{ gridColumn: "span 12", pt: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} mb={3}>
          Operational Status
        </Typography>
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 8" } }}>
        <FormTextField name="facilityType" label="Facility Type" control={control} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
        <FormSelect
          label="Facility Status"
          selectItems={[
            FacilityStatus.Open,
            FacilityStatus.Closed,
            FacilityStatus.Inactive,
            FacilityStatus.Planned,
          ].map((val) => ({
            value: val,
            display: val,
          }))}
          name="status"
          control={control}
        />
      </Box>
      <Box sx={{ gridColumn: "span 12" }}>
        <FormTextField name="statusReason" label="Facility Status Reason" control={control} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
        <DateField name="dateOpened" label="Date Opened" control={control} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
        <DateField name="dateClosed" label="Date Closed" control={control} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
        <DateField name="dateInactive" label="Date Inactive" control={control} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
        <FormTextField
          intOnly
          name="maxNumOccupants"
          label="Max Occupancy"
          control={control}
          disabled={!!getValues("isUnmanned")}
        />
      </Box>
      <Box
        sx={{
          gridColumn: { xs: "span 12", sm: "span 4" },
          alignSelf: "center",
        }}
      >
        <CheckboxField
          name="isUnmanned"
          label="Unmanned"
          control={control}
          checkboxProps={{
            onChange: (_, checked) => {
              if (checked) setValue("maxNumOccupants", 0);
            },
          }}
        />
      </Box>
      <Box
        sx={{
          gridColumn: { xs: "span 12", sm: "span 4" },
          alignSelf: "center",
        }}
      >
        <CheckboxField name="isColocated" label="Co-located" control={control} />
      </Box>
      {isColocated && (
        <Box sx={{ gridColumn: "span 12" }}>
          <Controller
            name="colocationReportingFacilityId"
            control={control}
            render={({ field, fieldState }) => (
              <FacilityPicker
                {...field}
                {...fieldState}
                value={colocationReportingFacility}
                defaultSearchTerm={`tenantId:${tenant?.id}`}
                label="Colocation Reporting Facility"
                disabledFacilitiesById={[data?.facility?.id ?? ""]}
                onChange={(fac) => {
                  setColocationReportingFacility(fac);
                  field.onChange(fac?.id ?? null);
                }}
              />
            )}
          />
        </Box>
      )}
    </Box>
  );
}

function PhysicalAddressFields() {
  return (
    <Box sx={gridSx}>
      <Box sx={{ gridColumn: "span 12" }}>
        <Typography variant="subtitle2" fontWeight={600} mb={3}>
          Physical Address
        </Typography>
      </Box>
      <AddressGrid type="physical" />
    </Box>
  );
}

export function MailingAddressFields() {
  const { control, setValue, watch } = useFormContext<EditBasicsFormData>();
  const isMailingAddressSameAsFacility = watch("isMailingAddressSameAsFacility");
  const previousIsMailingAddressSameAsFacilityRef = useRef<MailingAddressSelection>(undefined);
  const streetAddress1 = watch("streetAddress1");
  const streetAddress2 = watch("streetAddress2");
  const city = watch("city");
  const state = watch("state");
  const zip = watch("zip");
  const country = watch("country");
  const companyName = watch("companyName");
  const showMailingFields =
    isMailingAddressSameAsFacility !== true && isMailingAddressSameAsFacility !== "encamp";

  useEffect(() => {
    const mailingAddressUpdates = getMailingAddressUpdatesForSelection({
      isMailingAddressSameAsFacility,
      previousIsMailingAddressSameAsFacility: previousIsMailingAddressSameAsFacilityRef.current,
      streetAddress1,
      streetAddress2,
      city,
      state,
      zip,
      country,
      companyName,
    });
    previousIsMailingAddressSameAsFacilityRef.current = isMailingAddressSameAsFacility;

    if (mailingAddressUpdates == null) {
      return;
    }

    setValue("mailingStreetAddress1", mailingAddressUpdates.mailingStreetAddress1);
    setValue("mailingStreetAddress2", mailingAddressUpdates.mailingStreetAddress2);
    setValue("mailingCity", mailingAddressUpdates.mailingCity);
    setValue("mailingState", mailingAddressUpdates.mailingState);
    setValue("mailingZip", mailingAddressUpdates.mailingZip);
    setValue("mailingCountry", mailingAddressUpdates.mailingCountry);
    // Trigger validation once after all mailing fields have been synced so persisted
    // mailing-address issues clear immediately when selecting Encamp or same-as-facility.
    setValue("mailingCompanyName", mailingAddressUpdates.mailingCompanyName, {
      shouldValidate: true,
    });
  }, [
    isMailingAddressSameAsFacility,
    streetAddress1,
    streetAddress2,
    city,
    state,
    zip,
    country,
    companyName,
    setValue,
  ]);

  return (
    <Box sx={gridSx}>
      <Box sx={{ gridColumn: "span 12" }}>
        <Typography variant="subtitle2" fontWeight={600} mb={3}>
          Mailing Address
        </Typography>
      </Box>
      <Box sx={{ gridColumn: "span 12" }}>
        <RadioGroupField
          label="Is the mailing address the same as the facility address?"
          name="isMailingAddressSameAsFacility"
          control={control}
          radioOptions={[
            { label: "Yes", value: true },
            { label: "No", value: false },
            { label: "Use Encamp's mailing address", value: "encamp" },
          ]}
        />
      </Box>
      {showMailingFields && (
        <>
          <AddressGrid type="mailing" />
          <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
            <FormTextField name="mailingCompanyName" label="Company Name" control={control} />
          </Box>
        </>
      )}
    </Box>
  );
}

function BillingFields() {
  const { control } = useFormContext<EditBasicsFormData>();
  const { data } = useFacility();
  const canEditBillingContact = useCanEditFacilityBillingContact(data?.facility?.id);

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} mb={3}>
        Billing
      </Typography>
      <RadioGroupField
        label="Use Encamp as the facility's billing contact?"
        subtitle={
          canEditBillingContact
            ? "Indicate if you want Encamp to be used as the facility's billing contact for states that require a billing contact. If you answer No here, you can provide a Billing Contact in the Contacts step."
            : "Your role does not have permission to change the facility's billing contact."
        }
        radioOptions={[
          { value: true, label: "Yes" },
          { value: false, label: "No" },
        ]}
        name="useEncampBillingContact"
        control={control}
        disabled={!canEditBillingContact}
      />
    </Box>
  );
}
